import { More, FirstPage, ChevronLeft, ChevronRight, LastPage } from "@mui/icons-material";
import { Box, IconButton, Table, TableRow, TableBody, TableHead } from "@mui/material";
import { FC, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import {
    flexRender,
    getCoreRowModel,
    useReactTable,
    type ColumnDef,
    getSortedRowModel,
    type SortingState,
    getPaginationRowModel,
    type PaginationState,
    ColumnResizeMode,
    ColumnResizeDirection,
} from "@tanstack/react-table";

import "./kigyoInfoList.css";
import PrintDialog from '../printDialog/printDialog';
import "../css/style.css";
import { GreenButton, ItemLabelBox, GreenContainer } from './kigyoInfo.style';
import type { KigyoList } from "./kigyo.type";
import { KIGYO_LIST_ITEMS } from "./kigyoSelectItems";

import { sessionDisconnectedCheck } from '../../shared/api/sessionDisconnectedCheck';
import { fetchAuthSession } from 'aws-amplify/auth';
import { SearchCrudApiRequest } from '../../shared/model/apiRequestModel';
import crud from '../../shared/api/crud';
import { SystemErrorMsg, SystemErrorTitle } from '../../constants';
import { ConditionalTableBodyCell, ConditionalTableHeadCell } from "../search/inputSearchCondition.style";
import { BackdropLoading } from "../../shared/spinner/backdropLoading";

//PDF出力ダイアログ表示用の型
type Kind = "一覧";
type PrintInfo = {
    kind: Kind;
    start: number;
    end: number;
    total: number;
    sorting: SortingState;
    condition: Condition[] | undefined;
}

type DetailSort = {
    sorting?: SortingState;
    pageIndex?: number;
    searchConditions?: Condition[];
    searchItemsState?: { [key: string]: SearchItemState }
    resultCount?: number;
}

type Condition = {
    operator: string
    itemLabel: string
    count: number
    value: string
}

interface SearchItemState {
    itemCode: string;
    operator: 'AND' | 'OR';
    inputValue: string;
    inputLabel?: string;
    matchFlag?: boolean;
}

const screenId = "U050"

const KigyoInfoList: FC = () => {

    useEffect(() => {
        const interval = setInterval(async () => {
            // セッションの有効性確認
            if (await sessionDisconnectedCheck(screenId, "polling", navigate)) {
                return;
            }
        }, 30000);
        return () => clearInterval(interval);
    }, []);

    const navigate = useNavigate();
    const { state } = useLocation() as { state?: DetailSort };
    const searchConditions = state?.searchConditions;
    const searchItemsState = state?.searchItemsState;
    const resultCount = state?.resultCount === undefined ? 0 : Math.min(state?.resultCount, 1000);
    const [data, setData] = useState<KigyoList[]>([]);
    const [loading, setLoading] = useState(false);

    // 企業件数取得API呼び出し
    useEffect(() => {

        const fetchData = async () => {

            // セッションの有効性確認
            if (await sessionDisconnectedCheck(screenId, 'getKigyoInfoListData', navigate)) {
                return
            }

            const session = await fetchAuthSession();
            const email = session.tokens?.idToken?.payload?.email;
            const sessionId = session.tokens?.idToken?.payload?.sessionId;
            const accountInfoData = JSON.parse(session.tokens?.idToken?.payload?.accountInfoData?.toString() || "[]");
            const contract = JSON.parse(session?.tokens?.idToken?.payload.contractInfoData?.toString() || "[]");
            const contractForm = contract.length !== 0 && contract[0].contractForm;

            setLoading(true)
            const requests: SearchCrudApiRequest[] = Array.from({ length: Math.ceil(resultCount / 100) }, (_, i) => {
                const offset = i * 100;
                return {
                    sessionId: sessionId ? sessionId.toString() : '',
                    userMailAddress: email ? email.toString() : '',
                    processFlag: "0",
                    countFlag: "0",
                    contractForm: contractForm,
                    targetTableName: "T_SEARCH_RESULT_TMP",
                    selectItems: KIGYO_LIST_ITEMS,
                    conditions: [
                        {
                            targetColumn: "MAIL_ADDRESS",
                            targetValue: [email],
                        },
                    ],
                    conditionsOperator: "and",
                    orderBy: [
                        {
                            sortColumn: "URIAGE1",
                            sortRule: "DESC",
                        },
                        {
                            sortColumn: "KIGYO_CODE",
                            sortRule: "ASC",
                        },
                    ],
                    usingStartDate: contract.length !== 0 && accountInfoData[0].usingStartDate,
                    limit: 100,
                    offset: offset
                };
            });

            const responses = (await Promise.all(
                requests.map((r) => crud(r))
            )).flat();
            setLoading(false)

            //レスポンスが一つでも問題があればエラー画面遷移
            let hasResCDError = false;

            for (let i = 0; i < responses.length; i++) {
                if (responses[i].resCd !== "I0000") {
                    hasResCDError = true;
                    break;
                }
            }

            if (hasResCDError) {
                navigate("/Error", { state: { title: SystemErrorTitle, message: SystemErrorMsg } });
                return;
            }

            //レスポンスのデータをマージ
            const mergedData = [];

            for (let i = 0; i < responses.length; i++) {
                mergedData.push(...responses[i].data?.resultData);
            }

            setData(mergedData);
        }
        fetchData();

    }, []);

    const dispSearch = async () => {

        // セッションの有効性確認
        if (await sessionDisconnectedCheck(screenId, 'handleClickDispInputSearchCondition', navigate)) {
            return
        }

        navigate("/search", {
            state: {
                fromResult: searchConditions,
                searchItemsState: searchItemsState,
            }
        });
    }

    // String型はフリガナでソート
    const collator = new Intl.Collator("ja", { sensitivity: "base", numeric: true });

    // 数値で比較する項目
    const numberSortList = ["SHIHONKIN", "JUGYOINSU", "URIAGE1", "RIEKI1"]

    //第一ソート用関数
    const KigyoSortingFn = (primarySortKey: string) => (a: any, b: any) => {
        if (numberSortList.includes(primarySortKey)) {
            const valueA = a.original[primarySortKey]
            const valueB = b.original[primarySortKey]
            if(valueA === valueB ) return 0;
            if ((valueA === "" || valueA === null) && (valueB !== "" || valueB !== null)) return -1;
            if ((valueA !== "" || valueA !== null) && (valueB === "" || valueB === null)) return 1;
            return valueA - valueB
        } else {
            return collator.compare(a.original[primarySortKey] ?? "", b.original[primarySortKey] ?? "");
        }
    }

    //第二ソートは企業コード昇順で固定
    const secondarySortKey: keyof KigyoList = "KIGYO_CODE";

    const firstDescMap: Record<string, boolean> = {
        "SHOGO_KANJI": false,
        "JUSHO": false,
        "DAIHYOSHA_KANJI": false,
        "KIGYO_CODE": false,
        "YUBIN_NO": false,
        "SHIHONKIN": true,
        "JUGYOINSU": true,
        "URIAGE1": true,
        "RIEKI1": true,
    }

    // ヘッダー右揃えカラム
    const rightAlignColumns = ["資本金", "従業員数", "最新売上", "最新利益"]

    //カラム定義
    const columns: ColumnDef<KigyoList>[] = [
        {
            id: "rowNumber",
            header: "通番",
            enableSorting: false, //通番はソート不可
            //通番は常に順番に表示
            cell: ({ table, row }) => {
                const sortedRows = table.getSortedRowModel().rows;
                const idx = sortedRows.findIndex(r => r.id === row.id);
                return idx + 1;
            },
            size: 20,
            minSize: 20
        },
        {
            accessorKey: "SHOGO_KANJI",
            header: "商号",
            sortDescFirst: false,
            sortingFn: KigyoSortingFn("SHOGO_KANA"),
            size: 50,
            minSize: 25
        },
        {
            accessorKey: "JUSHO",
            header: "所在地",
            sortDescFirst: false,
            sortingFn: KigyoSortingFn("JUSHO_CODE"),
            size: 60,
            minSize: 25
        },
        {
            accessorKey: "DAIHYOSHA_KANJI",
            header: "代表者",
            sortDescFirst: false,
            sortingFn: KigyoSortingFn("DAIHYOSHA_KANA"),
            size: 35,
            minSize: 25
        },
        {
            accessorKey: "KIGYO_CODE",
            header: "企業コード",
            sortDescFirst: false,
            sortingFn: KigyoSortingFn("KIGYO_CODE"),
            size: 40,
            minSize: 25
        },
        {
            accessorKey: "YUBIN_NO",
            header: "郵便番号",
            sortDescFirst: false,
            sortingFn: KigyoSortingFn("YUBIN_NO"),
            cell: (info) => {
                const value = String(info.getValue() ?? "");

                if (value == "") return;
                return `〒${value}`;
            },
            size: 35,
            minSize: 25
        },
        {
            accessorKey: "SHIHONKIN",
            header: "資本金",
            sortingFn: KigyoSortingFn("SHIHONKIN"),
            cell: (info) => {
                if (info.getValue() == null) return;

                const value = Number(info.getValue() ?? 0);
                return value.toLocaleString("ja-JP");
            },
            size: 40,
            minSize: 25
        },
        {
            accessorKey: "JUGYOINSU",
            header: "従業員数",
            sortingFn: KigyoSortingFn("JUGYOINSU"),
            cell: (info) => {
                if (info.getValue() == null) return;

                const value = Number(info.getValue() ?? 0);
                return value.toLocaleString("ja-JP");
            },
            size: 40,
            minSize: 25
        },
        {
            accessorKey: "URIAGE1",
            header: "最新売上",
            sortingFn: KigyoSortingFn("URIAGE1"),
            cell: (info) => {
                if (info.getValue() == null) return;
                const value = Number(info.getValue() ?? 0);
                return　info.row.original.MIKOMI_SUITEI_KUBUN1 === "2" ? `◎${value.toLocaleString("ja-JP")}` : `${value.toLocaleString("ja-JP")}`;
            },
            size: 50,
            minSize: 25
        },
        {
            accessorKey: "RIEKI1",
            header: "最新利益",
            sortingFn: KigyoSortingFn("RIEKI1"),
            cell: (info) => {
                if (info.getValue() == null) return;

                const value = Number(info.getValue() ?? 0);
                return value.toLocaleString("ja-JP");
            },
            size: 40,
            minSize: 25
        },
        {
            id: "detail",
            header: "詳細",
            enableSorting: false,

            cell: (ctx) => {
                const rowsNow = ctx.table.getRowModel().rows;
                const posInPage = rowsNow.findIndex(r => r.id === ctx.row.id);
                const { pageIndex, pageSize } = ctx.table.getState().pagination;

                //通番
                const recordId = pageIndex * pageSize + posInPage + 1;

                //現在のソート情報
                const { sorting } = ctx.table.getState();

                return (
                    <IconButton
                        color="warning"
                        onClick={async () => {
                            // セッションの有効性確認
                            if (await sessionDisconnectedCheck(screenId, 'handleClickDispKigyoInfoDtail', navigate)) {
                                return
                            }

                            navigate(`/result/detail`, { state: { recordId, sorting, pageIndex, searchConditions, searchItemsState, resultCount } });
                        }}
                    >
                        <More />
                    </IconButton>
                );
            },
            size: 20,
            minSize: 20
        },
    ];

    const onSort = (primaryItem: string) => {
        setSorting((prev) => {
            const nextDesc = prev[0].id === primaryItem ? !prev[0].desc : (firstDescMap[primaryItem] ?? false);
            return [
                { id: primaryItem, desc: nextDesc },
                { id: secondarySortKey, desc: false },
            ];
        });
    };

    //デフォルトは最新売上の降順、詳細から戻るときは前の状態を保存
    const defaultSorting = [{ id: "URIAGE1", desc: true }, { id: "KIGYO_CODE", desc: false }];

    const [sorting, setSorting] = useState<SortingState>(
        state?.sorting && state.sorting.length > 0 ? state.sorting : defaultSorting
    );

    const primarySortId = sorting[0]?.id;

    //ページング設定、詳細から戻るときは前のページを表示
    const defaultPageIndex = 0;

    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: state?.pageIndex ?? defaultPageIndex,
        pageSize: 100,
    });

    const [columnResizeMode,] = useState<ColumnResizeMode>('onChange')
    const [columnResizeDirection,] = useState<ColumnResizeDirection>('ltr')
    const [columnSizing, setColumnSizing] = useState({});

    const table = useReactTable({
        data,
        columns,
        state: { sorting, pagination, columnSizing },
        initialState: { sorting, pagination },
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
        onColumnSizingChange: setColumnSizing,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        enableSortingRemoval: false,
        enableMultiSort: true,
        columnResizeMode,
        columnResizeDirection,
        enableColumnResizing: true,
        autoResetPageIndex: false,
    });

    const { pageIndex, pageSize } = table.getState().pagination;
    const pageCount = table.getPageCount();

    //ページング時の最初と最後の件数と検索結果トータル
    const allRows = table.getPrePaginationRowModel().rows;
    const total = allRows.length;
    const pageStart = total === 0 ? 0 : pageIndex * pageSize + 1;
    const pageEnd = Math.min(pageStart + pageSize - 1, total);

    const [printDialogOpen, setPrintDialogOpen] = useState(false);
    const [printInfo, setPrintInfo] = useState<PrintInfo>({
        kind: "一覧",
        start: 0,
        end: 0,
        total: 0,
        sorting: defaultSorting,
        condition: state?.searchConditions,
    });

    const handleOpen = async () => {
        // セッションの有効性確認
        if (await sessionDisconnectedCheck(screenId, 'handleOpen', navigate)) {
            return
        }

        setPrintInfo({
            kind: "一覧",
            start: pageStart,
            end: pageEnd,
            total: total,
            sorting: sorting,
            condition: state?.searchConditions,
        });

        setPrintDialogOpen(true);
    };

    //データ取得するまでPDF出力ダイアログは押下不可とする
    const [isPrintDialog, setIsPrintDialog] = useState(false);

    useEffect(() => {
        if (total === 0 || total === null) {
            setIsPrintDialog(false);
            return;
        };

        setIsPrintDialog(true);
    }, [total]);

    if (data === null) return null;

    return (
        <>
            <BackdropLoading open={loading} />
            <Box className="headerLabel">
                検索結果一覧
            </Box>
            <div className="pager">
                <span>
                    {pageStart}～{pageEnd}／{total}件
                </span>
                <button onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()}>
                    <FirstPage />
                </button>
                <button onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
                    <ChevronLeft />
                </button>
                <button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
                    <ChevronRight />
                </button>
                <button onClick={() => table.setPageIndex(pageCount - 1)} disabled={!table.getCanNextPage()}>
                    <LastPage />
                </button>
            </div>
            <table className="tableStyle" onContextMenu={(e) => { e.preventDefault() }} style={{ direction: table.options.columnResizeDirection, userSelect: "none" }} >
                <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                            {/*ヘッダー押下でソートする*/}
                            {headerGroup.headers.map((header) => {
                                const canSort = header.column.getCanSort();
                                const dir = header.column.getIsSorted();

                                return (
                                    <th
                                        colSpan={header.colSpan}
                                        style={{
                                            paddingLeft: "10px",
                                            fontSize: "16px",
                                            width: header.getSize(),
                                            position: 'relative',
                                            padding: '0px',
                                            minWidth: '20px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}
                                        key={header.id}
                                        className={canSort ? "sortable" : undefined}
                                        onClick={header.column.getCanSort() ? () => { table.setPageIndex(0); onSort(header.column.id) } : undefined}
                                    >
                                        <div style={{ display: "flex", justifyContent: rightAlignColumns.includes(header.column.columnDef.header?.toString() || "") ? 'flex-end' : 'space-between' }}>
                                            <div style={{ padding: '8px' }}>
                                                {flexRender(header.column.columnDef.header, header.getContext(),)}
                                                {primarySortId === header.column.id && dir === "asc" && <span style={{ color: "#767676", fontSize: "18px" }}>↑</span>}
                                                {primarySortId === header.column.id && dir === "desc" && <span style={{ color: "#767676", fontSize: "18px" }}>↓</span>}

                                            </div>
                                            <div
                                                {...{
                                                    onMouseDown: header.getResizeHandler(),
                                                    onTouchStart: header.getResizeHandler()
                                                }}
                                                className={`resizer ${header.column.getIsResizing() ? "isResizing" : ""}`}
                                            />
                                        </div>
                                    </th>
                                )
                            })}
                        </tr>
                    ))}
                </thead >
                <tbody>
                    {table.getRowModel().rows.map((row) => (
                        <tr key={row.id}>
                            {row.getVisibleCells().map((cell, i) => (
                                <td key={cell.id}
                                    /* 列ごとに寄せる位置を設定 */
                                    className={
                                        i === 0 || i === 10 ? "center" :
                                            i === 1 || i === 2 || i === 3 || i === 4 || i === 5 ? "left" :
                                                i === 6 || i === 7 || i === 8 || i === 9 ? "right" : ""
                                    }
                                    style={{ fontSize: "15px" }}
                                >
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table >
            <div className="pager">
                <span>
                    {pageStart}～{pageEnd}／{total}件
                </span>
                <button onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()}>
                    <FirstPage />
                </button>
                <button onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
                    <ChevronLeft />
                </button>
                <button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
                    <ChevronRight />
                </button>
                <button onClick={() => table.setPageIndex(pageCount - 1)} disabled={!table.getCanNextPage()}>
                    <LastPage />
                </button>
            </div>
            <ItemLabelBox>
                検索条件
            </ItemLabelBox>
            <Box marginTop={2}>
                <GreenContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <ConditionalTableHeadCell width='10%'>選択条件</ConditionalTableHeadCell>
                                <ConditionalTableHeadCell width='20%'>件数</ConditionalTableHeadCell>
                                <ConditionalTableHeadCell width='70%'>入力値</ConditionalTableHeadCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {searchConditions?.map((condition: Condition) => (
                                <TableRow>
                                    <ConditionalTableBodyCell width='10%'>{condition.itemLabel}</ConditionalTableBodyCell>
                                    <ConditionalTableBodyCell width='20%'>{condition.count.toLocaleString()}</ConditionalTableBodyCell>
                                    <ConditionalTableBodyCell sx={{ wordBreak: 'break-all' }} width='70%'>{condition.value}</ConditionalTableBodyCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </GreenContainer>
            </Box>

            <div className="buttons">
                <GreenButton variant="contained" color="secondary" onClick={() => dispSearch()}>検索条件</GreenButton>
                <GreenButton variant="contained" color="secondary" disabled={!isPrintDialog} onClick={handleOpen}>PDF出力</GreenButton>
            </div>

            <PrintDialog isOpen={printDialogOpen} handleValue={printInfo} handleClose={() => setPrintDialogOpen(false)} />
        </>
    );
}
export default KigyoInfoList;
